import { ReactNode, useState } from 'react'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import Cookies from 'js-cookie'
import { TinyTalkLogoFull } from './Logo'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { LogOut, Menu, Users, FileText, Baby, History } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { ThemeToggle } from './ThemeToggle'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { User, UserRole } from '@/types/dashboard.types'
import { PARENT_NAV_ITEMS, ADMIN_NAV_ITEMS } from '@/constants/dashboard.constants'

interface DashboardLayoutProps {
  children: ReactNode
  user: User
}

export const DashboardLayout = ({ children, user }: DashboardLayoutProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const navItems = user.role === UserRole.ADMIN ? ADMIN_NAV_ITEMS : PARENT_NAV_ITEMS

  const quickLinks: { label: string; path: string; Icon: LucideIcon }[] =
    user.role === UserRole.ADMIN
      ? [
        { label: 'Manage Users', path: '/dashboard/admin/users', Icon: Users },
        { label: 'Manage Questionnaires', path: '/dashboard/admin/questionnaires', Icon: FileText },
      ]
      : [
        { label: 'My Children', path: '/dashboard/children', Icon: Baby },
        { label: 'Assessment History', path: '/dashboard/history', Icon: History },
      ]

  const normalizePath = (p: string): string => {
    const trimmed = p.replace(/\/+$/, '')
    return trimmed === '' ? '/' : trimmed
  }

  /**
   * Mark only the nav item that matches the current route.
   * `/dashboard` must be an exact match — otherwise every child route (/dashboard/reports/..., etc.)
   * incorrectly highlights Dashboard.
   */
  const isActivePath = (path: string): boolean => {
    const pathname = normalizePath(location.pathname)
    const itemPath = normalizePath(path)

    if (itemPath === '/dashboard') {
      return pathname === '/dashboard'
    }

    // Assessment reports live under /dashboard/reports — treat as part of History for nav state.
    if (itemPath === '/dashboard/history') {
      return pathname === '/dashboard/history' || pathname.startsWith('/dashboard/reports')
    }

    return pathname === itemPath || pathname.startsWith(`${itemPath}/`)
  }

  const getUserInitials = (): string => {
    const first = user.firstName?.[0] || 'U'
    const last = user.lastName?.[0] || ''
    return `${first}${last}`
  }

  const handleLogout = () => {
    const tokenKey = import.meta.env.VITE_TOKEN_NAME || 'token'
    localStorage.removeItem(tokenKey)
    Cookies.remove(tokenKey)
    setMobileNavOpen(false)
    navigate({ to: '/auth/login' })
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="container mx-auto flex h-14 items-center justify-between gap-2 px-4 sm:h-16 sm:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2 md:gap-8">
            {/* Mobile: slide-out menu (nav + logout) */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 md:hidden"
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="flex w-[min(100vw-1.5rem,20rem)] flex-col p-0 sm:max-w-sm">
                <SheetHeader className="border-b border-border px-4 py-4 text-left">
                  <SheetTitle className="text-base">Menu</SheetTitle>
                  <p className="truncate text-sm text-muted-foreground">{user.email}</p>
                  <p className="text-xs capitalize text-muted-foreground">{user.role.toLowerCase()}</p>
                </SheetHeader>
                <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-4" aria-label="Mobile navigation">
                  {navItems.map((item) => (
                    <SheetClose key={item.path} asChild>
                      <Link to={item.path}>
                        <Button
                          type="button"
                          variant={isActivePath(item.path) ? 'default' : 'ghost'}
                          className="h-auto w-full justify-start gap-2 py-3 text-left"
                          aria-current={isActivePath(item.path) ? 'page' : undefined}
                        >
                          <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                          <span>{item.label}</span>
                        </Button>
                      </Link>
                    </SheetClose>
                  ))}
                </nav>
                <SheetFooter className="border-t border-border">
                  <Button
                    type="button"
                    variant="destructive"
                    className="w-full gap-2"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4" />
                    Log out
                  </Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>

            <Link
              to="/dashboard"
              className="min-w-0 shrink"
              onClick={() => setMobileNavOpen(false)}
            >
              <TinyTalkLogoFull className="h-9 sm:h-10" />
            </Link>

            {/* Desktop Navigation */}
            <nav
              className="hidden md:flex md:items-center md:gap-1"
              role="navigation"
              aria-label="Main navigation"
            >
              {navItems.map((item) => (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={isActivePath(item.path) ? 'default' : 'ghost'}
                    size="sm"
                    className="gap-2"
                    aria-label={`Navigate to ${item.label}`}
                    aria-current={isActivePath(item.path) ? 'page' : undefined}
                  >
                    <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                    <span>{item.label}</span>
                  </Button>
                </Link>
              ))}
            </nav>
          </div>

          {/* User Menu */}
          <div className="flex shrink-0 items-center gap-1 sm:gap-2 md:gap-4">
            <div className="hidden flex-col items-end md:flex">
              <span className="text-sm font-medium">
                {user.firstName} {user.lastName}
              </span>
              <span className="text-xs text-muted-foreground capitalize">
                {user.role.toLowerCase()}
              </span>
            </div>

            <ThemeToggle />

            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="hidden items-center gap-2 md:inline-flex"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 shrink-0 rounded-full sm:h-10 sm:w-10">
                  <Avatar>
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs sm:text-sm">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {quickLinks.map((item) => (
                  <DropdownMenuItem key={item.path} asChild>
                    <Link to={item.path} className="flex items-center gap-2">
                      <item.Icon className="h-4 w-4 shrink-0" aria-hidden />
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content — stable scrollbar gutter per layout rules */}
      <main
        className="container mx-auto overflow-x-auto px-4 py-5 sm:px-6 sm:py-6"
        style={{ scrollbarGutter: 'stable' }}
      >
        {children}
      </main>
    </div>
  )
}
